/**
 * Sanitizes Teams message HTML before it is written into the exported offline
 * viewer.
 *
 * This is a real security boundary, not a formatting nicety. `message.body.content`
 * is HTML authored by whoever sent the message — including people outside the
 * user's control — and it is about to become part of a local HTML file the user
 * (or someone they hand `export.log` and a folder to) opens directly in a
 * browser. Writing it through unsanitized would let a message sender plant
 * script that executes when the export is viewed: a stored-XSS vector baked
 * into a "backup" file with a long shelf life. `no user content ever leaves the
 * browser` (ARCHITECTURE.md §1) is about outbound network calls; it does not
 * make injected script in a local file safe.
 *
 * Approach: parse as a DOM fragment, walk it, and rebuild a new tree keeping
 * only an allowlisted set of tags and attributes. Nothing is carried over by
 * default — allowlist, not blocklist — so an unrecognised tag (script, style,
 * iframe, svg, form, object, embed, custom elements) is dropped, including its
 * children, rather than risking an interpretation this list didn't anticipate.
 *
 * Teams also emits its own non-standard tags inline: <at> for @mentions,
 * <emoji> for emoji, <attachment> for card/file references. These are
 * rewritten to plain text before the generic walk, so an @mention still reads
 * as "@Name" instead of vanishing along with every other unknown tag.
 */

const ALLOWED_TAGS = new Set([
    "p",
    "br",
    "b",
    "strong",
    "i",
    "em",
    "u",
    "s",
    "span",
    "div",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "code",
    "pre",
    "table",
    "thead",
    "tbody",
    "tr",
    "td",
    "th",
    "img",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6"
]);

const SAFE_URL_SCHEMES = new Set(["http:", "https:", "mailto:"]);

function isSafeLinkHref(href: string): string | null {
    try {
        // Relative to a fixed, harmless base so a bare "javascript:alert(1)"
        // still parses as an (unsafe) absolute URL rather than throwing.
        const url = new URL(href, "https://invalid.example/");
        return SAFE_URL_SCHEMES.has(url.protocol) ? url.toString() : null;
    } catch {
        return null;
    }
}

/**
 * Replaces Teams' custom inline tags with their plain-text meaning, in place,
 * before the allowlist walk runs. Must run first — the generic walk has no
 * entry for <at>/<emoji>/<attachment> and would otherwise drop them.
 */
function rewriteTeamsTags(root: ParentNode, attachmentNames: ReadonlyMap<string, string>): void {
    for (const at of Array.from(root.querySelectorAll("at"))) {
        at.replaceWith(document.createTextNode(`@${at.textContent ?? ""}`));
    }

    for (const emoji of Array.from(root.querySelectorAll("emoji"))) {
        const alt = emoji.getAttribute("alt");
        emoji.replaceWith(document.createTextNode(alt ?? emoji.textContent ?? ""));
    }

    for (const attachment of Array.from(root.querySelectorAll("attachment"))) {
        const id = attachment.getAttribute("id");
        const name = id ? attachmentNames.get(id) : undefined;
        attachment.replaceWith(document.createTextNode(name ? `[${name}]` : "[attachment]"));
    }
}

function sanitizeElement(
    el: Element,
    out: Node,
    assetUrlMap: ReadonlyMap<string, string>
): void {
    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
        // Drop the element and its subtree. This is deliberate for tags like
        // script/style/iframe — their text content is not meant to be visible
        // markup either, and keeping it risks reflecting attacker-controlled
        // text in an unexpected place.
        return;
    }

    if (tag === "img") {
        // Never carry over the original src. It is either a bearer-token-gated
        // Graph URL (useless outside this session) or an arbitrary external
        // URL — and loading an external URL from an opened-offline export file
        // would silently phone home. Only images we already fetched and wrote
        // to assets/ are kept, via a lookup on the original absolute URL.
        const originalSrc = el.getAttribute("src");
        const localSrc = originalSrc ? assetUrlMap.get(originalSrc) : undefined;
        if (!localSrc) {
            return;
        }
        const img = document.createElement("img");
        img.setAttribute("src", localSrc);
        const alt = el.getAttribute("alt");
        if (alt) {
            img.setAttribute("alt", alt);
        }
        out.appendChild(img);
        return;
    }

    const clean = document.createElement(tag);

    if (tag === "a") {
        const href = el.getAttribute("href");
        const safe = href ? isSafeLinkHref(href) : null;
        if (safe) {
            clean.setAttribute("href", safe);
            clean.setAttribute("rel", "noopener noreferrer");
        }
        // No safe href: still render as a plain (unlinked) span of its content
        // rather than dropping the whole element, by simply not setting href.
    }

    for (const child of Array.from(el.childNodes)) {
        sanitizeNode(child, clean, assetUrlMap);
    }

    out.appendChild(clean);
}

function sanitizeNode(node: Node, out: Node, assetUrlMap: ReadonlyMap<string, string>): void {
    if (node.nodeType === Node.TEXT_NODE) {
        out.appendChild(document.createTextNode(node.textContent ?? ""));
        return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
        sanitizeElement(node as Element, out, assetUrlMap);
    }
    // Comments, processing instructions, etc. are dropped silently.
}

/**
 * Escapes plain text for safe inclusion in HTML markup — for values that sit
 * *outside* a sanitizeMessageBody() fragment (sender names, chat topics) but
 * still originate from Graph data a user other than the exporter's owner may
 * control. A display name or chat topic containing "<script>" is unlikely,
 * but not impossible, and costs nothing to guard against here.
 */
export function escapeHtml(text: string): string {
    const p = document.createElement("p");
    p.textContent = text;
    return p.innerHTML;
}

export interface SanitizeOptions {
    /** Original absolute hostedContents/attachment URL -> local assets/ path. */
    assetUrlMap?: ReadonlyMap<string, string>;
    /** attachment element id -> a human name, for the [name] placeholder. */
    attachmentNames?: ReadonlyMap<string, string>;
}

/**
 * Sanitizes a Teams message body into a safe HTML fragment string.
 *
 * `contentType: "text"` bodies are escaped via textContent and wrapped, never
 * parsed as HTML — there is nothing to sanitize and no reason to risk it.
 */
export function sanitizeMessageBody(
    content: string,
    contentType: "text" | "html",
    options: SanitizeOptions = {}
): string {
    if (contentType === "text") {
        const p = document.createElement("p");
        p.textContent = content;
        return p.outerHTML;
    }

    const assetUrlMap = options.assetUrlMap ?? new Map();
    const attachmentNames = options.attachmentNames ?? new Map();

    // A <template> parses its innerHTML as inert content — no scripts run,
    // no images load, no network activity — which is what makes it safe to
    // hand attacker-controlled markup to the parser at all.
    const template = document.createElement("template");
    template.innerHTML = content;

    rewriteTeamsTags(template.content, attachmentNames);

    const out = document.createDocumentFragment();
    for (const child of Array.from(template.content.childNodes)) {
        sanitizeNode(child, out, assetUrlMap);
    }

    const wrapper = document.createElement("div");
    wrapper.appendChild(out);
    return wrapper.innerHTML;
}
